// simpleBgmPlayer.js
// CRITICAL: iOS Safari-compatible BGM player using HTML5 <audio> element
// Web Audio API BufferSource doesn't work reliably on iOS Safari after navigation

const DEFAULT_BGM_URL =
	"https://test.goodglows.com/media/story/glowloop_spacehardcore_1.mp3";
const QUEST_BGM_URL =
	"https://test.goodglows.com/media/story/quest_BGM_longer.mp3";

let bgmAudioElement = null;
let isInitialized = false;
let currentBgmUrl = DEFAULT_BGM_URL;

/**
 * Get or create the persistent BGM audio element
 * CRITICAL: This element is created ONCE and reused forever
 * This is the ONLY way to persist audio across iOS Safari navigation
 */
export function getBgmElement() {
	if (!bgmAudioElement) {
		console.log("🎵 Creating persistent BGM audio element");

		bgmAudioElement = new Audio();
		bgmAudioElement.loop = true;
		bgmAudioElement.preload = "auto";
		bgmAudioElement.crossOrigin = "anonymous";

		// Store in window to prevent garbage collection
		window.__glowBgmAudio = bgmAudioElement;

		// Add event listeners for debugging
		bgmAudioElement.addEventListener("play", () => {
			console.log("✅ BGM audio element started playing");
		});

		bgmAudioElement.addEventListener("pause", () => {
			console.log("⏸️ BGM audio element paused");
		});

		bgmAudioElement.addEventListener("ended", () => {
			console.log(
				"🔚 BGM audio element ended (should not happen with loop=true)"
			);
		});

		bgmAudioElement.addEventListener("error", (e) => {
			console.error("❌ BGM audio element error:", e);
		});
	}

	return bgmAudioElement;
}

/**
 * Initialize and start BGM
 * MUST be called from a user gesture on iOS Safari
 */
export async function startBgm(
	url = DEFAULT_BGM_URL,
	volume = 0.1,
	muted = false
) {
	const audio = getBgmElement();

	console.log("🎵 Starting BGM:", {
		url,
		volume,
		muted,
		currentSrc: audio.src,
	});

	// Only change source if different
	if (audio.src !== url && !audio.src.endsWith(url)) {
		console.log("🔄 Changing BGM source to:", url);
		audio.src = url;
		currentBgmUrl = url;
	}

	audio.volume = muted ? 0 : volume;
	audio.muted = muted;

	try {
		// CRITICAL: On iOS Safari, this MUST be called from a user gesture
		await audio.play();
		isInitialized = true;
		console.log("✅ BGM started successfully");
		return true;
	} catch (error) {
		console.error("❌ Failed to start BGM:", error);
		return false;
	}
}

export function getBgmCurrentTime() {
	const audio = getBgmElement();
	return audio.currentTime || 0;
}

export function setBgmCurrentTime(seconds = 0) {
	const audio = getBgmElement();
	const targetTime = Math.max(0, Number(seconds) || 0);

	const apply = () => {
		try {
			audio.currentTime = targetTime;
		} catch (err) {
			console.warn("⚠️ Failed to set BGM currentTime:", err);
		}
	};

	if (audio.readyState >= 1 && !Number.isNaN(audio.duration)) {
		apply();
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		const handler = () => {
			audio.removeEventListener("loadedmetadata", handler);
			audio.removeEventListener("canplay", handler);
			apply();
			resolve();
		};

		audio.addEventListener("loadedmetadata", handler);
		audio.addEventListener("canplay", handler);
	});
}

/**
 * Resume BGM if paused
 * Works after navigation if BGM was already started
 */
export async function resumeBgm() {
	const audio = getBgmElement();

	if (!audio.src) {
		console.warn("⚠️ Cannot resume BGM - no source set");
		return false;
	}

	if (!audio.paused) {
		console.log("✅ BGM already playing");
		return true;
	}

	console.log("▶️ Resuming BGM...");

	try {
		// NOTE: An earlier version forced `navigator.audioSession.type = "playback"`
		// here to fix a BGM-channel bug after speech recognition. That hint LOCKS
		// iOS into the playback category and stops it auto-promoting to
		// play-and-record on subsequent getUserMedia() calls — the camera/mic
		// permission prompt never appears. The actual BGM-channel fix lives in
		// ComposePostQuest.stopSpeechRecognition (resume on `onend` only, never
		// during `stop()`), which doesn't interfere with iOS's session routing.
		await audio.play();
		console.log("✅ BGM resumed successfully");
		return true;
	} catch (error) {
		console.error("❌ Failed to resume BGM:", error);
		return false;
	}
}

/**
 * Pause BGM
 */
export function pauseBgm() {
	const audio = getBgmElement();
	if (!audio.paused) {
		console.log("⏸️ Pausing BGM");
		audio.pause();
	}
}

/**
 * Set BGM volume (0.0 to 1.0)
 */
export function setBgmVolume(volume) {
	const audio = getBgmElement();
	audio.volume = Math.max(0, Math.min(1, volume));
}

/**
 * Set BGM muted state
 */
export function setBgmMuted(muted) {
	const audio = getBgmElement();
	console.log(
		`🔊 setBgmMuted(${muted}) - volume BEFORE: ${audio.volume}, muted BEFORE: ${audio.muted}`
	);
	audio.muted = muted;
	console.log(
		`🔊 setBgmMuted(${muted}) - volume AFTER: ${audio.volume}, muted AFTER: ${audio.muted}`
	);
	console.log(muted ? "🔇 BGM muted" : "🔊 BGM unmuted");
}

/**
 * Toggle BGM muted state
 */
export function toggleBgmMuted() {
	const audio = getBgmElement();
	audio.muted = !audio.muted;
	console.log(audio.muted ? "🔇 BGM muted" : "🔊 BGM unmuted");
	return audio.muted;
}

/**
 * Check if BGM is playing
 */
export function isBgmPlaying() {
	const audio = getBgmElement();
	return !audio.paused && audio.currentTime > 0 && !audio.ended;
}

/**
 * Check if BGM is muted
 */
export function isBgmMuted() {
	const audio = getBgmElement();
	return audio.muted;
}

/**
 * Fade BGM volume over duration
 */
export function fadeBgmTo(targetVolume, duration = 500) {
	const audio = getBgmElement();
	const startVolume = audio.volume;
	const volumeDiff = targetVolume - startVolume;
	const steps = 20;
	const stepTime = duration / steps;
	let currentStep = 0;

	const interval = setInterval(() => {
		currentStep++;
		if (currentStep >= steps) {
			audio.volume = targetVolume;
			clearInterval(interval);
			console.log("✅ BGM fade complete");
		} else {
			const progress = currentStep / steps;
			audio.volume = startVolume + volumeDiff * progress;
		}
	}, stepTime);
}

/**
 * Get current BGM info
 */
export function getBgmInfo() {
	const audio = getBgmElement();
	return {
		src: audio.src,
		volume: audio.volume,
		muted: audio.muted,
		paused: audio.paused,
		currentTime: audio.currentTime,
		duration: audio.duration,
		isPlaying: isBgmPlaying(),
	};
}

export { DEFAULT_BGM_URL, QUEST_BGM_URL };

/**
 * Cleanup and reset the simple BGM player
 * Call this when switching to a different BGM system or on sign-out
 */
export function cleanupSimpleBgm() {
	console.log("🧹 Cleaning up simple BGM player");

	const audio = bgmAudioElement || window.__glowBgmAudio;

	if (audio) {
		// Pause and clear the audio
		audio.pause();
		audio.src = "";
		audio.load(); // Force reset

		// Remove from window reference
		if (window.__glowBgmAudio) {
			delete window.__glowBgmAudio;
		}
	}

	// Reset module state
	bgmAudioElement = null;
	isInitialized = false;
	currentBgmUrl = DEFAULT_BGM_URL;

	console.log("✅ Simple BGM cleanup complete");
}
